import React, { ChangeEvent, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';
import { Filters } from '@/types/Filters';
import { CustomerReceivable } from '@/types/CustomerReceivable.interface';
import { useTableFilters } from '@/hooks/useTableFilters';
import NoDataFound from '@/components/NoDataFound';
import { debounce } from '@/utils/helpers';
import { formatCurrency } from '@/utils/formatters';
import { formatDateToDayMonthYear } from '@/utils/dateFormatters';
import { maskTaxId } from '@/utils/taxIdMask';
import {
  RECEIVABLE_STATUSES,
  RECEIVABLE_STATUS_COLOR
} from '@/utils/receivables';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';

interface ReceivablesTableProps {
  receivables: CustomerReceivable[];
  loading: boolean;
  totalCount: number;
  onFilterChange: (filters: Filters) => void;
  onSelectReceivable: (receivable: CustomerReceivable) => void;
  /** Extra work for the refresh button (e.g. reloading the page summary). */
  onRefresh?: () => void;
  rowsPerPageOptions?: number[];
  pageKey?: string;
}

const ReceivablesTable = ({
  receivables,
  loading,
  totalCount,
  onFilterChange,
  onSelectReceivable,
  onRefresh,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS,
  pageKey
}: ReceivablesTableProps) => {
  const { t } = useTranslation();

  const {
    filters,
    setFilters,
    handleSearch: handleSearchFilter
  } = useTableFilters({
    initialFilters: { status: undefined, search: '', limit: 10, page: 0 },
    onFilterChange,
    pageKey
  });

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    onFilterChange({ ...filters, page: newPage });
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newLimit = parseInt(event.target.value, 10);
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 0 }));
    onFilterChange({ ...filters, page: 0, limit: newLimit });
  };

  // Status chips apply immediately (no popover "Apply" step), so they must
  // update the local filter state AND notify the page, like pagination does.
  const handleStatusChip = (status: string | undefined): void => {
    setFilters((prev) => ({ ...prev, status, page: 0 }));
    onFilterChange({ ...filters, status, page: 0 });
  };

  // One debounced function for the lifetime of the table, so a pending timer is
  // never orphaned by a re-render. It reads the live handler (which closes over
  // the current filters) through a ref, so a status chip or page size changed
  // while the search is pending is not overwritten by stale filters.
  const searchHandlerRef = useRef(handleSearchFilter);
  searchHandlerRef.current = handleSearchFilter;
  const handleSearch = useMemo(
    () => debounce((value: string) => searchHandlerRef.current(value), 500),
    []
  );

  const tableHeaders: Array<{
    id: string;
    label: string;
    align?: 'left' | 'center' | 'right';
  }> = [
    { id: 'customer', label: t('receivables.column.customer') },
    { id: 'taxId', label: t('receivables.column.taxId') },
    {
      id: 'amount',
      label: t('receivables.column.amount'),
      align: 'right'
    },
    {
      id: 'paidAmount',
      label: t('receivables.column.paidAmount'),
      align: 'right'
    },
    {
      id: 'remaining',
      label: t('receivables.column.remaining'),
      align: 'right'
    },
    {
      id: 'status',
      label: t('receivables.column.status'),
      align: 'center'
    },
    { id: 'documentNo', label: t('receivables.column.documentNo') },
    { id: 'date', label: t('receivables.column.date') }
  ];

  return (
    <Container disableGutters maxWidth={false} sx={{ maxWidth: '95%' }}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Card>
            <CardHeader
              action={
                <Box display="flex" alignItems="center">
                  <FormControl sx={{ minWidth: 260 }}>
                    <OutlinedInput
                      placeholder={t('receivables.search.placeholder')}
                      defaultValue={filters.search}
                      onChange={(e) => handleSearch(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchOutlinedIcon />
                        </InputAdornment>
                      }
                      size="small"
                    />
                  </FormControl>
                </Box>
              }
              title={
                <Box display="flex" alignItems="center">
                  <Typography variant="h4" component="span">
                    {t('receivables.list.title')}
                  </Typography>
                  <Tooltip arrow title={t('refresh.list')}>
                    <IconButton
                      aria-label={t('refresh.list')}
                      color="primary"
                      onClick={() => {
                        onFilterChange({ ...filters });
                        onRefresh?.();
                      }}
                      sx={{ ml: 1 }}
                    >
                      <RefreshTwoToneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            <Divider />
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ px: 2, py: 1.5 }}
              role="group"
              aria-label={t('receivables.filter.status')}
            >
              <Chip
                label={t('receivables.filter.all')}
                color={filters.status ? 'default' : 'primary'}
                variant={filters.status ? 'outlined' : 'filled'}
                onClick={() => handleStatusChip(undefined)}
                size="small"
              />
              {RECEIVABLE_STATUSES.map((status) => {
                const selected = filters.status === status;
                return (
                  <Chip
                    key={status}
                    label={t(`receivables.status.${status}`)}
                    color={RECEIVABLE_STATUS_COLOR[status]}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => handleStatusChip(status)}
                    size="small"
                  />
                );
              })}
            </Stack>
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header.id} align={header.align ?? 'left'}>
                        {header.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length}>
                        <Box p={2} display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : receivables.length > 0 ? (
                    receivables.map((receivable) => (
                      <TableRow
                        hover
                        key={receivable.id}
                        tabIndex={0}
                        onClick={() => onSelectReceivable(receivable)}
                        onKeyDown={(event) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onSelectReceivable(receivable);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            noWrap
                          >
                            {receivable.customer?.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {maskTaxId(receivable.customer?.taxId)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" noWrap>
                            {formatCurrency(Number(receivable.amount))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" noWrap>
                            {formatCurrency(Number(receivable.paidAmount))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" noWrap>
                            {formatCurrency(Number(receivable.remaining))}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(`receivables.status.${receivable.status}`)}
                            color={RECEIVABLE_STATUS_COLOR[receivable.status]}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {receivable.documentNo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {formatDateToDayMonthYear(
                              receivable.documentDate ?? receivable.createdAt
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <NoDataFound
                      message={t('receivables.no.data')}
                      colSpan={tableHeaders.length}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box p={2}>
              <TablePagination
                component="div"
                count={totalCount || 0}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={filters.page || 0}
                rowsPerPage={filters.limit || 10}
                rowsPerPageOptions={rowsPerPageOptions}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReceivablesTable;
